import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { RecordsQuery, RecordsView, RecordSortField, MaterialType, RecordStatus } from '@svetoch/contracts';

const views: RecordsView[] = ['all', 'drafts', 'incomplete', 'favorites'];
const sorts: RecordSortField[] = ['systemNumber', 'title', 'author', 'year', 'publication', 'materialType', 'status', 'updatedAt'];
const materials: MaterialType[] = ['book', 'map', 'journal', 'other'];
const statuses: RecordStatus[] = ['complete', 'incomplete'];
const safeNumber = (value: string | null, fallback: number, max = Number.MAX_SAFE_INTEGER) => {
  const n = Number(value); return Number.isSafeInteger(n) && n > 0 && n <= max ? n : fallback;
};

export function useCatalogQuery() {
  const [params, setParams] = useSearchParams();
  const query = useMemo<RecordsQuery>(() => {
    const pageSize = Number(params.get('pageSize'));
    const view = params.get('view') as RecordsView;
    const sortBy = params.get('sortBy') as RecordSortField;
    const materialType = params.get('materialType') as MaterialType;
    const status = params.get('status') as RecordStatus;
    const year = safeNumber(params.get('year'), 0, 9999);
    return {
      q: params.get('q')?.slice(0, 500) || undefined,
      author: params.get('author')?.slice(0, 300) || undefined,
      year: year || undefined,
      page: safeNumber(params.get('page'), 1),
      pageSize: [10, 20, 50].includes(pageSize) ? pageSize as 10 | 20 | 50 : 10,
      view: views.includes(view) ? view : 'all',
      sortBy: sorts.includes(sortBy) ? sortBy : 'systemNumber',
      sortDirection: params.get('sortDirection') === 'asc' ? 'asc' : 'desc',
      materialType: materials.includes(materialType) ? materialType : undefined,
      status: statuses.includes(status) ? status : undefined
    };
  }, [params]);
  const updateQuery = useCallback((patch: Partial<RecordsQuery>, resetPage = true) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined || value === '' || (key === 'view' && value === 'all')) next.delete(key);
        else next.set(key, String(value));
      }
      if (resetPage) next.delete('page');
      return next;
    }, { replace: true });
  }, [setParams]);
  const resetFilters = useCallback(() => {
    updateQuery({ q: undefined, author: undefined, year: undefined, materialType: undefined, status: undefined, view: 'all' });
  }, [updateQuery]);
  return { query, updateQuery, resetFilters };
}
